import { format, parseISO, differenceInDays, addDays, isWeekend } from 'date-fns';

// Format date to display format
export const formatDate = (dateString: string): string => {
  return format(parseISO(dateString), 'MMM dd, yyyy');
};

// Format date and time
export const formatDateTime = (dateTimeString: string): string => {
  return format(parseISO(dateTimeString), 'MMM dd, yyyy hh:mm a');
};

// Format time only
export const formatTime = (dateTimeString: string): string => {
  // Parse the timestamp - it's already in Philippine timezone from getPhilippineDateTime
  // So we need to subtract 8 hours to get the actual Philippine time for display
  const storedDate = parseISO(dateTimeString);
  const actualPhilippineTime = new Date(storedDate.getTime() - (8 * 60 * 60 * 1000));
  return format(actualPhilippineTime, 'HH:mm');
};

// Calculate days between two dates (inclusive)
export const calculateDaysBetween = (startDate: string, endDate: string): number => {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  return differenceInDays(end, start) + 1; // +1 to include both start and end dates
};

// Calculate working days between two dates (excluding weekends)
export const calculateWorkingDays = (startDate: string, endDate: string): number => {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  let workingDays = 0;
  let currentDate = start;

  while (currentDate <= end) {
    if (!isWeekend(currentDate)) {
      workingDays++;
    }
    currentDate = addDays(currentDate, 1);
  }

  return workingDays;
};

// Get current date in ISO format (YYYY-MM-DD)
export const getCurrentDate = (): string => {
  return format(new Date(), 'yyyy-MM-dd');
};

// Get current date and time in ISO format
export const getCurrentDateTime = (): string => {
  return new Date().toISOString();
};

// Get current date and time in Philippine timezone (UTC+8)
export const getPhilippineDateTime = (): string => {
  const now = new Date();
  // Add 8 hours for Philippine timezone (UTC+8)
  const philippineTime = new Date(now.getTime() + (8 * 60 * 60 * 1000));
  return philippineTime.toISOString();
};

// Get current date in Philippine timezone
export const getPhilippineDate = (): string => {
  const now = new Date();
  // Add 8 hours for Philippine timezone (UTC+8)
  const philippineTime = new Date(now.getTime() + (8 * 60 * 60 * 1000));
  return philippineTime.toISOString().split('T')[0];
};

// Convert datetime-local input to Philippine timezone ISO string
export const convertToPhilippineTime = (dateTimeLocal: string): string => {
  if (!dateTimeLocal) return '';
  // datetime-local input is treated as Philippine time, just convert to ISO
  const localDate = new Date(dateTimeLocal);
  return localDate.toISOString();
};

// Convert ISO string from database (Philippine time) to datetime-local format for input display
export const convertFromPhilippineTime = (isoString: string): string => {
  if (!isoString) return '';
  // The stored time is already in the correct format, just convert to datetime-local format
  const date = new Date(isoString);
  // Convert to local datetime-local format (YYYY-MM-DDTHH:mm)
  return date.toISOString().slice(0, 16);
};

// Get first day of current month
export const getFirstDayOfMonth = (): string => {
  const date = new Date();
  return format(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd');
};

// Get last day of current month
export const getLastDayOfMonth = (): string => {
  const date = new Date();
  return format(new Date(date.getFullYear(), date.getMonth() + 1, 0), 'yyyy-MM-dd');
};

// Get first day of previous month
export const getFirstDayOfPreviousMonth = (): string => {
  const date = new Date();
  return format(new Date(date.getFullYear(), date.getMonth() - 1, 1), 'yyyy-MM-dd');
};

// Get last day of previous month
export const getLastDayOfPreviousMonth = (): string => {
  const date = new Date();
  return format(new Date(date.getFullYear(), date.getMonth(), 0), 'yyyy-MM-dd');
};